import React from 'react';
import Input from '../../../../../Utility/FormElements/Input';
import useGlobals from '../../../../../../Hooks/useGlobals';

interface Parameter {
    name: string;
    type: string;
    required: boolean;
    description?: string;
    schema?: {
        type: string;
        enum?: string[];
    };
}

interface ParametersSectionProps {
    selectedEndpoint: {
        parameters?: Parameter[];
    } | null;
    parameters: { [key: string]: string | number | boolean };
    onParameterChange: (name: string, value: string) => void;
}

const ParametersSection: React.FC<ParametersSectionProps> = ({ selectedEndpoint, parameters, onParameterChange }) => {
    const { i18n, themes, selectedTheme } = useGlobals();

    const themeColor = themes?.[selectedTheme]?.themeColor ?? 'light';
    if (!selectedEndpoint || !selectedEndpoint.parameters || selectedEndpoint.parameters.length === 0) {
        return null;
    }

    return (
        <div>
            <div className="my-4">
                <h4>{i18n.translate({ id: 'testing.parameters.title', sourceName: 'OpenAPI' })}</h4>
            </div>
            <div className="space-y-3">
                {selectedEndpoint.parameters.map((param) =>
                    param.name !== 'requestBody' ? (
                        <div key={param.name}>
                            <label className="block text-layout-textcolor">
                                <div className="flex items-center mb-2">
                                    <h5 className="m-0 p-0">{param.name}</h5>
                                    {!param.required && <span className="ml-2 text-sm text-gray-500">(Optional)</span>}
                                </div>
                                {param.description && <p className="text-sm text-gray-500 mb-2">{param.description}</p>}
                                {param.schema?.enum && param.schema.enum.length > 0 ? (
                                    <select
                                        value={String(parameters[param.name] || '')}
                                        onChange={(e) => onParameterChange(param.name, e.target.value)}
                                        disabled={param.schema.enum.length === 1}
                                        className={`w-full px-4 py-2 text-layout-textcolor bg-input-backgroundColor border border-input-borderColor rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            param.schema.enum.length === 1 ? 'opacity-60 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        {Array.from(new Set(param.schema.enum)).map((option: string) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <Input
                                        inputType={param.type === 'number' ? 'number' : 'text'}
                                        value={parameters[param.name] || ''}
                                        onChange={(value) => onParameterChange(param.name, value)}
                                    />
                                )}
                            </label>
                        </div>
                    ) : (
                        <textarea
                            value={String(parameters[param.name] || '')}
                            onChange={(e) => onParameterChange(param.name, e.target.value)}
                            placeholder={`${param.type}${param.required ? ' (required)' : ' (optional)'}`}
                            className="w-full resize-none text-layout-textcolor bg-input-backgroundColor"
                            spellCheck={false}
                            style={{
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                                fontSize: '14px',
                                lineHeight: '1.4285714286',
                                padding: '16px',
                                margin: '0',
                                border: 'none',
                                outline: 'none',
                                whiteSpace: 'pre' as const,
                                wordWrap: 'break-word' as const,
                                overflowWrap: 'break-word' as const,
                                tabSize: 2,
                                overflow: 'auto' as const,
                                boxSizing: 'border-box' as const,
                                width: '100%',
                                height: '100%',
                                minHeight: '300px',
                                maxHeight: '500px',
                                resize: 'none' as const,
                                caretColor: themeColor === 'light' ? '#24292e' : '#e6edf3',
                                borderRadius: '6px',
                            }}
                        />
                    ),
                )}
            </div>
        </div>
    );
};

export default ParametersSection;
